import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  const article = articles.find((a) => a.slug === slug);
  const relatedArticles = article
    ? articles.filter((a) => a.category === article.category && a.slug !== article.slug).slice(0, 3)
    : [];

  if (!article) {
    return (
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-20 text-center">
        <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
          <i className="ri-file-search-line text-3xl text-foreground-400"></i>
        </div>
        <h1 className="text-2xl font-bold font-heading text-foreground-950 mb-2">文章未找到</h1>
        <p className="text-foreground-600 mb-6">您访问的文章不存在或已被移除</p>
        <Link
          to="/category/all"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap"
        >
          <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
          返回文章列表
        </Link>
      </div>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.image,
    datePublished: article.date,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    publisher: {
      '@type': 'Organization',
      name: '大满贯App',
      logo: {
        '@type': 'ImageObject',
        url: 'https://damanjuapp.com/logo.png',
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://damanjuapp.com/article/${article.slug}`,
    },
  };

  return (
    <>
      <SEOHead
        title={article.title}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://damanjuapp.com/article/${article.slug}`}
        ogType="article"
        ogImage={article.image}
        articleDate={article.date}
        articleAuthor={article.author}
        schema={articleSchema}
      />
      <Breadcrumb />

      <article className="max-w-4xl mx-auto px-4 md:px-6 py-10 md:py-14">
        {/* Header */}
        <header className="mb-8">
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <Link
              to={`/category/${article.category}`}
              className="px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium hover:bg-primary-200 transition-colors duration-200 whitespace-nowrap"
            >
              {article.categoryName}
            </Link>
            <div className="flex items-center gap-2 text-xs text-foreground-400 flex-wrap">
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line w-3 h-3 flex items-center justify-center"></i>
                {article.date}
              </span>
              <span>·</span>
              <span className="flex items-center gap-1">
                <i className="ri-user-line w-3 h-3 flex items-center justify-center"></i>
                {article.author}
              </span>
            </div>
          </div>
          <h1 className="text-2xl md:text-4xl font-bold font-heading text-foreground-950 mb-4 leading-tight">
            {article.title}
          </h1>
          <p className="text-foreground-600 text-base leading-relaxed">{article.excerpt}</p>
        </header>

        {/* Featured Image */}
        <figure className="mb-10 rounded-2xl overflow-hidden">
          <img
            src={article.image}
            alt={article.title}
            title={`${article.title} - 大满贯App`}
            className="w-full h-auto object-cover"
          />
        </figure>

        {/* Content */}
        <div className="prose-custom mb-12">
          {article.content.split('\n\n').map((paragraph, idx) => (
            <p key={idx} className="text-foreground-700 text-base leading-relaxed mb-5">
              {paragraph.trim()}
            </p>
          ))}
        </div>

        {/* Tags */}
        <div className="flex flex-wrap items-center gap-2 mb-10 pt-6 border-t border-background-200/70">
          <span className="text-sm font-medium text-foreground-600">
            <i className="ri-price-tag-3-line w-4 h-4 flex items-center justify-center inline mr-1"></i>
            标签：
          </span>
          {article.tags.map((tag) => (
            <span
              key={tag}
              className="px-3 py-1 rounded-full bg-background-100 text-foreground-600 text-xs hover:bg-background-200 transition-colors duration-200 cursor-pointer whitespace-nowrap"
            >
              {tag}
            </span>
          ))}
        </div>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="border-t border-background-200/70 pt-10">
            <h2 className="text-xl font-bold font-heading text-foreground-950 mb-6 flex items-center gap-2">
              <i className="ri-links-line w-5 h-5 flex items-center justify-center text-primary-500"></i>
              相关文章
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {relatedArticles.map((ra) => (
                <Link
                  key={ra.slug}
                  to={`/article/${ra.slug}`}
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  className="group rounded-xl overflow-hidden bg-background-100 hover:shadow-lg transition-all duration-300"
                >
                  <div className="h-40 overflow-hidden">
                    <img
                      src={ra.image}
                      alt={ra.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-4">
                    <h3 className="text-sm font-bold font-heading text-foreground-950 line-clamp-2 group-hover:text-primary-500 transition-colors duration-200 mb-2">
                      {ra.title}
                    </h3>
                    <span className="text-xs text-foreground-400">{ra.date}</span>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Back Link */}
        <div className="mt-10 text-center">
          <Link
            to="/category/all"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full border-2 border-background-200 text-foreground-600 font-medium text-sm hover:border-primary-500 hover:text-primary-500 transition-all duration-200 whitespace-nowrap"
          >
            <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
            返回文章列表
          </Link>
        </div>
      </article>
    </>
  );
}